import type { Prisma } from "@prisma/client";
import { constructionSiteAbilityControlSelect, constructionSiteWithDetailsSelector, constructionSiteDocumentTypes, constructionSiteActionSelector, constructionSiteCreationSelector, constructionSiteCreationDetailsSelector, constructionSiteSyndicInfoSelector, constructionSiteFullDetailsSelector, constructionSiteSubmissionSelector, clientTypes, actionTypes, constructionSiteWarrantyOfferSelector, constructionSiteStatuses,  constructionSiteDocumentSelect, constructionSiteDocumentTypesThatNeedParticipantLinkToBeConsideredAsReferenced, gfaStatuses, constructionSiteClientDetailsSelector } from "@dune/vars";
import { PaginationMeta } from "../common/pagination.types";

export type ClientType = (typeof clientTypes)[number];
export type ActionType = (typeof actionTypes)[number];

export type ConstructionSiteStatus = (typeof constructionSiteStatuses)[number];
export type GFAStatus = (typeof gfaStatuses)[number];

export type ConstructionSiteDocumentType = (typeof constructionSiteDocumentTypes)[number];

export type ConstructionSiteDocumentTypeThatNeedParticipantLinkToBeConsideredAsReferenced = (typeof constructionSiteDocumentTypesThatNeedParticipantLinkToBeConsideredAsReferenced)[number];

export type ConstructionSiteAbilityControl = Prisma.ConstructionSiteGetPayload<{
    select: typeof constructionSiteAbilityControlSelect
}>

export type ConstructionSiteAction = Prisma.ConstructionSiteActionGetPayload<{
	select: typeof constructionSiteActionSelector;
}>;

export type ConstructionSiteClientDetails = Prisma.ConstructionSiteClientDetailsGetPayload<{
	select: typeof constructionSiteClientDetailsSelector;
}>;

export type ConstructionSiteFullDetails = Prisma.ConstructionSiteGetPayload<{
	select: typeof constructionSiteFullDetailsSelector;
}>;

export type ConstructionSiteCreation = Prisma.ConstructionSiteGetPayload<{
	select: typeof constructionSiteCreationSelector;
}>;

export type ConstructionSiteCreationDetails = Prisma.ConstructionSiteDetailsGetPayload<{
	select: typeof constructionSiteCreationDetailsSelector;
}>;

export type ConstructionSiteList = Prisma.ConstructionSiteGetPayload<{
    select: typeof constructionSiteWithDetailsSelector;
}>

export type ConstructionSiteSyndicInfo = Prisma.ConstructionSiteGetPayload<{
	select: typeof constructionSiteSyndicInfoSelector;
}>;

export type ConstructionSiteSubmissionPayload = Prisma.ConstructionSiteSubmissionGetPayload<{
    select: typeof constructionSiteSubmissionSelector;
}>;

export interface ConstructionSiteSubmissionValidationPayload {
  validated: boolean | null;
  addReservesEnabled: boolean | null;
  reserves: string[];
}

export type ConstructionSiteWarrantyOffer = Prisma.ConstructionSiteWarrantyOfferGetPayload<{
	select: typeof constructionSiteWarrantyOfferSelector;
}>

export type SelectedBinderWarranty = Prisma.SelectedBinderWarrantyGetPayload<{
	select: {
		id: true;
		binderWarranty: {
			select: {
				id: true;
				warrantyType: true;
				binderAmendment: {
					select: {
						binder: {
							select: {
								insurer: {
									select: {
										society: {
											select: {
												companyName: true;
											};
										};
									};
								};
							};
						};
					};
				};
			};
		};
	};
}>;

export type BuildingDetailFieldValue = { 
	name: string; 
	indication?: string; 
};

export type BuildingDetails = {
	label: string;
	separator?: string;
	values: BuildingDetailFieldValue[];
};

export type ActionTypeCheckbox = {
	label: string;
	value: string;
	typeCheckboxesToHide?: string[];
	buildingTypeCheckboxesToHide?: string[];
};

export type ActionBuildingTypeCheckbox = {
	label: string;
	value: string;
	details?: BuildingDetails[];
};

export type AppartmentDetails = {
	fromFloor: string;
	toFloor: string;
	surface: string;
};

export type IndividualHouseDetails = {
	floorsCount: string;
	basementsCount: string;
	surface: string;
	habitationsCount: string;
};

export type BaseBuildingDetails = {
	buildingsCount: string;
	maxFloors: string;
	maxBasements: string;
	surface: string;
};

export type BuildingWithHabitationsDetails = BaseBuildingDetails & {
	habitationsCount: string;
};

export type MixtBuildingDetails = BuildingWithHabitationsDetails & {
	habitationsSize: string;
	officesSize: string;
	commercialBuildingsSize: string;
};

export type BuildingDetailsUnion =
	| AppartmentDetails
	| IndividualHouseDetails
	| BaseBuildingDetails
	| BuildingWithHabitationsDetails
	| MixtBuildingDetails;

export const ConstructionSiteFileType = {
	KBIS: "KBIS",
	IDENTITY: "IDENTITY",
	RIB: "RIB",
	RC: "RC",
	GUARANTEE: "GUARANTEE",
	LCB: "LCB",
	LOGO: "LOGO",
	OTHER: "OTHER",
} as const;

export type ConstructionSiteFileType = typeof ConstructionSiteFileType[keyof typeof ConstructionSiteFileType];

export const constructionSiteFileTypeLabels: Record<ConstructionSiteFileType, string> = {
	KBIS: "KBIS",
	IDENTITY: "Pièce d'identité",
	RIB: "RIB",
	RC: "RC Pro",
	GUARANTEE: "Garantie",
	LCB: "LCB-FT",
	LOGO: "Logo",
	OTHER: "Autre",
};

export const ConstructionSiteStatusLabels: Record<ConstructionSiteStatus |GFAStatus, string> = {
	NEW: "Nouveau",
	IN_PROGRESS: "En cours",
	VALIDATED: "Validé",
	TO_RECEIVE: "À réceptionner",
	REFUSED: "Refusé",
	RECEIVED: "Réceptionné",
	TO_ANALYZE: "À analyser",
	UNDER_WARRANTY: "Sous garantie",
} as const;

export type ConstructionSiteStatusLabelValues = typeof ConstructionSiteStatusLabels[keyof typeof ConstructionSiteStatusLabels];

export interface SendFormToClientRequest {
	clientEmail: string;
	brokerId: number;
	clientData: {
		clientType: "USER" | "SOCIETY";
		clientUserIds?: number[];
		clientSocietyId?: number;
		clientUsers?: Array<{
			id?: number;
			firstname: string;
			lastname: string;
			email?: string;
			streetNumber?: string;
			streetName?: string;
			postalCode?: string;
			city?: string;
		}>;
		clientSociety?: {
			id?: number;
			siren?: string | null;
			denomination: string;
			streetNumber?: string;
			streetName?: string;
			postalCode?: string;
			city?: string;
			isSyndic?: boolean;
			hasBrokerageFees?: boolean | string;
			brokerageFees?: number | null;
			hasUserAccess?: boolean | string;
			userAccess?: {
				firstname: string;
				lastname: string;
				email: string;
			};
		};
		clientIsMOA: string;
		moaClientType: "USER" | "SOCIETY";
		moaClientUserId?: number;
		moaClientSocietyId?: number;
		moaClientUser?: {
			firstname: string;
			lastname: string;
		};
		moaClientSociety?: {
			siren?: string | null;
			denomination: string;
			streetNumber?: string;
			streetName?: string;
			postalCode?: string;
			city?: string;
		};
	};
}

export interface SendFormToClientResponse {
	constructionSiteId: number;
	completionUrl: string;
	message: string;
}

// Raw Prisma type from selector
export type ConstructionSiteDocumentRaw = Prisma.FilesOnConstructionSiteGetPayload<{
	select: typeof constructionSiteDocumentSelect;
}>;

// Flattened type for easier frontend usage  
export type ConstructionSiteDocument = {
	id: string;
	name: string;
	type: ConstructionSiteDocumentType;
	status: string;
	isDeleted?: boolean;
	warrantyType?: string | null;
	insurerType?: string | null;
	bucket: string;
	key: string;
	mimeType: string;
	isReferenced?: boolean;
	createdAt: string;
	updatedAt: string;
	author?: {
		firstname: string;
		lastname: string;
		role: string;
		subrole: string | null;
	} | null;
};

export interface ConstructionSiteDocumentPaginatedResponse {
	data: ConstructionSiteDocument[];
	meta: PaginationMeta;
}

export interface UploadConstructionSiteDocumentData {
	constructionSiteId: number;
	name: string;
	key: string;
	bucket: string;
	mimeType: string;
	type: ConstructionSiteDocumentType;
}

export interface ConstructionSiteIntervenant {
	id: number;
	type: "DESIGNER" | "BUILDER" | "MATERIAL";
	society?: {
		id: number;
		denomination: string;
		siren: string | null
	} | null;
	foreignSociety?: {
		id: number;
		denomination: string;
		country: string;
	} | null;
	designerTypes?: ("ARCHITECTE" | "BUREAU_ETUDE" | "CONTROLEUR_TECHNIQUE" | "GEOTECHNICIEN" | "MAITRE_OEUVRE")[];
	materialTypes?: string[];
	amount?: number;
	justificationDocuments?: string[];
}

export interface ConstructionSiteIntervenants {
	builders: ConstructionSiteIntervenant[];
	designers: ConstructionSiteIntervenant[];
	materials: ConstructionSiteIntervenant[];
}

export interface ConstructionSiteForClientCompletion {
	constructionSiteId: number;
	brokerSociety: {
		id: number;
		denomination: string;
		logo?: string | null;
	};
}

export interface PublicConstructionSiteDetailsDto {
	streetNumber?: string | null | undefined;
	streetName: string;
	postalCode: string;
	city: string;
	rga?: number;
	sismic?: number;
	throughSyndic?: boolean;
	actions: {
		types?: string[];
		buildingType?: string;
		hasComplementaryActions?: boolean;
		complementaryActions?: {
			types: string[];
			buildingType?: string;
		};
		buildingDetails?: Record<string, any>;
		underpinningExistingFoundations?: boolean;
		interventionOnLoadBearingStructure?: boolean;
		socialLandlordRental?: boolean;
	};
	totalAmountTTC?: number;
	totalAmountHT?: number;
	startDate: Date;
	deliveryDate: Date;
	cityHallStartDate?: Date;
	interventions: {
		architect: Record<string, boolean>;
		moe: Record<string, boolean>;
		soil_study: Record<string, boolean>;
		technical_control: Record<string, boolean>;
		consulting: Record<string, boolean>;
	};
	ccmi?: boolean;
	nonUsualMethods: boolean;
	noOtherOperations?: boolean;
	projectName?: string;
	operationType?: string;
	processesType?: string;
}

export type OfferFileDisplay = {
	id: string;
	createdAt: string;
	warrantyType: string | null | undefined;
	insurerType: string | null | undefined;
	files: ConstructionSiteDocument[];
	isDeleted?: boolean;
};
